import { useFormik } from "formik";
import { Button } from "primereact/button";
import { useLazyLoadQuery } from "react-relay";
import * as Yup from "yup";
import { ValidatedField } from "@components/ValidatedField";
import { SelectMultipleRootNodesField } from "@features/educational-offer/select-multiple-root-nodes-field";
import { SelectMultipleRootNodesFieldState } from "@features/educational-offer/select-multiple-root-nodes-field/select-multiple-root-nodes-field.types";
import { inputFieldClass } from "@features/educational-offer/select-multiple-root-nodes-form/select-multiple-root-nodes-form.styles";
import { useTranslation } from "@i18n/hooks";
import { selectMultipleRootNodesForm_PublishedRootQuery } from "@relay/selectMultipleRootNodesForm_PublishedRootQuery.graphql";
import { QUERY } from "./select-multiple-root-nodes-form.graphql";
import {
	SelectMultipleRootNodesFormProps,
	SelectMultipleRootNodesFormState,
} from "./select-multiple-root-nodes-form.types";

export const SelectMultipleRootNodesForm = ({
	defaultValue,
	submitButtonLabel,
	onRootNodesSelected,
	disabled,
}: SelectMultipleRootNodesFormProps) => {
	const { t } = useTranslation(["common", "validation"]);

	const data = useLazyLoadQuery<selectMultipleRootNodesForm_PublishedRootQuery>(
		QUERY,
		{},
		{ fetchPolicy: "store-or-network" },
	);

	const formik = useFormik<SelectMultipleRootNodesFormState>({
		initialValues: {
			publishedRoots: defaultValue?.map((id) => ({ id })),
		},
		validationSchema: Yup.object().shape({
			publishedRoots: Yup.array()
				.of(
					Yup.object().shape({
						id: Yup.string(),
						title: Yup.string(),
					}),
				)
				.min(1, t("validation:courseRequired"))
				.required(t("validation:selectAtLeastOneCourse")),
		}),
		onSubmit: (data, { setSubmitting }) => {
			onRootNodesSelected(data.publishedRoots!.map((node) => node.id!));
			setSubmitting(false);
		},
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			<ValidatedField<SelectMultipleRootNodesFormState, SelectMultipleRootNodesFieldState>
				className={inputFieldClass}
				name={"publishedRoots"}
				label={t("common:course.selectOneCourse")}
				formikConfig={formik}
				component={(renderConfig) => (
					<SelectMultipleRootNodesField {...renderConfig} queryFragmentRef={data} />
				)}
			/>
			<Button
				disabled={!formik.isValid || disabled}
				label={submitButtonLabel ?? t("common:course.addCourse")}
				type="submit"
			/>
		</form>
	);
};
